/* ============================================================
   Quill&Steel — Sword cursor: dark zone detection
   ------------------------------------------------------------
   Tags anything that paints its own background with .qs-dark-zone or
   .qs-light-zone, so the cursor swaps to the white-outlined Dusk variant over
   dark and the brown Parchment one over cream.

   Three things get measured, because a page can be dark at any of them:
     - the body, for pages whose whole background is dark (the pricing page)
     - each section, for the usual dark/cream alternation
     - anything marked .qs-surface, for a panel that reverses its band's
       treatment (the cream pricing cards inside the dark pricing band)

   Nesting resolves through custom property inheritance rather than selector
   specificity, so the nearest tagged ancestor wins and a light card inside a
   dark band inside a dark page comes out right. See sword-cursor.css.

   Measured rather than hardcoded: this store alternates dark and light
   sections, and the backgrounds are theme-editor settings. A hardcoded list
   would be wrong the first time a background is changed in the admin.

   Runs once per section on load (and again when the theme editor rebuilds
   one). No loop, no per-frame work, no listeners left behind.
   ============================================================ */

(function () {
  "use strict";

  // Nothing to do where there is no pointer to style.
  if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

  // Perceived brightness, 0-255. Below this the white outline reads better
  // than the brown one. 128 is the midpoint; the brand green (#0c0f00) sits
  // at 30 and the cream (#DFDAD3) at 219, so nothing is near the boundary.
  var DARK_BELOW = 128;

  function brightness(color) {
    var m = color && color.match(/[\d.]+/g);
    if (!m || m.length < 3) return null;
    // Fully transparent tells us nothing about what is painted.
    if (m.length > 3 && parseFloat(m[3]) === 0) return null;
    return 0.299 * +m[0] + 0.587 * +m[1] + 0.114 * +m[2];
  }

  // The background that actually shows through for a section. Some sections
  // paint it on the wrapper, some on an inner element (image-with-text sets
  // --bg-body on a child), and some are transparent and show the page behind.
  function sectionBackground(section) {
    var b = brightness(getComputedStyle(section).backgroundColor);
    if (b !== null) return b;

    var inner = section.querySelector(
      ".image-with-text, .hero-portal, [style*='--bg-body'], [class*='__inner']"
    );
    if (inner) {
      // The element's OWN inline --bg-body, not the computed one. Custom
      // properties inherit, and head-variables.liquid sets --bg-body on :root
      // as the theme-wide body colour, so getComputedStyle reports that cream
      // on every element on the page. Reading it computed made this branch
      // answer "light" for any section with an __inner child, which is all of
      // them, and is why the features and pricing bands kept the brown
      // outline. image-with-text, the section this branch exists for, writes
      // --bg-body inline, which is what [style*='--bg-body'] above looks for.
      var v = inner.style.getPropertyValue("--bg-body").trim();
      if (v) {
        // A custom property holds an author value like "#0c0f00"; resolve it
        // to rgb() by letting the browser parse it.
        var probe = document.createElement("span");
        probe.style.cssText = "position:absolute;visibility:hidden;background:" + v;
        document.body.appendChild(probe);
        var rb = brightness(getComputedStyle(probe).backgroundColor);
        probe.remove();
        if (rb !== null) return rb;
      }
      var ib = brightness(getComputedStyle(inner).backgroundColor);
      if (ib !== null) return ib;
    }

    // Shopify's .shopify-section wrapper never paints anything itself. The
    // sections written for this store put their background colour on their own
    // root div inside that wrapper, so the wrapper measures transparent while
    // the band on screen is dark. Reading the children before giving up is what
    // makes the features and pricing pages come out dark; without it both fell
    // through to the body below, which is #fff, and took the brown outline.
    //
    // Two things this loop has to get right: the unpainted children come
    // first, because Shopify emits the section's <link> and <script> tags
    // ahead of the div that shows, so skip anything not rendered. And the
    // body is excluded, because its children are the sections themselves and
    // walking them would hand the page the first section's colour, not its own.
    if (section !== document.body) {
      for (var c = 0; c < section.children.length; c++) {
        var cs = getComputedStyle(section.children[c]);
        if (cs.display === "none" || cs.visibility === "hidden") continue;
        var cb = brightness(cs.backgroundColor);
        if (cb !== null) return cb;
      }
    }

    // Transparent all the way: it shows whatever the page background is.
    return brightness(getComputedStyle(document.body).backgroundColor);
  }

  // Both classes are set explicitly rather than only the dark one. A section
  // that measures light has to say so, or it would silently inherit the dark
  // treatment from a dark body above it.
  function tag(el) {
    var b = sectionBackground(el);
    if (b === null) return;
    var dark = b < DARK_BELOW;
    el.classList.toggle("qs-dark-zone", dark);
    el.classList.toggle("qs-light-zone", !dark);
  }

  function tagAll(scope) {
    var root = scope || document;

    // The page itself first. On a wholly dark page the gaps between sections
    // belong to no section at all, and were left on the brown outline.
    if (!scope) tag(document.body);

    var nodes = root.querySelectorAll(
      ".shopify-section, footer, .footer, .qs-surface"
    );
    for (var i = 0; i < nodes.length; i++) tag(nodes[i]);

    // The hero paints its background on an inner element and is always dark.
    var hero = root.querySelector(".hero-portal");
    if (hero) {
      hero.classList.add("qs-dark-zone");
      hero.classList.remove("qs-light-zone");
    }
  }

  function boot() {
    tagAll();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Run again at load. A deferred script executes before DOMContentLoaded, and
  // some sections (the footer here) have not had their background resolved by
  // then, so a single early pass reads them as transparent and leaves them
  // untagged. Tagging is an idempotent toggle, so repeating it is free.
  if (document.readyState !== "complete") {
    window.addEventListener("load", boot, { once: true });
  }

  document.addEventListener("shopify:section:load", function (e) {
    tagAll(e.target);
  });
})();
